// eslint-disable-next-line @github-ui/github-monorepo/filename-convention
import type {CopilotSurveyProps} from '@github-ui/copilot-survey-card'
import {noop} from '@github-ui/noop'
import type {RemoveFromConnectionOperation} from '@github-ui/optimistic-connection'
import type {SettleTarget} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import type {SubIssueSidePanelItem} from '@github-ui/sub-issues/sub-issue-types'
import type {PreloadedQuery, UseQueryLoaderLoadQueryOptions} from 'react-relay/hooks'

import type {ItemIdentifier} from '../types/issue'
import type {IssueViewerViewQuery} from './__generated__/IssueViewerViewQuery.graphql'

export type IssueViewerPreloadedQueries = {
  issueViewerViewQuery: PreloadedQuery<IssueViewerViewQuery>
}

export type ViewNavState = {
  viewName: string
  items: ItemIdentifier[]
  returnUrl?: string
  scrollY?: number
  totalCount?: number
  backItemCount?: number
  pendingFocusDirection?: 'prev' | 'next'
  pendingNavSource?: 'shortcut' | 'click' | 'activate'
}

export type IssueDeletionConnection = {
  ownerId: string
  connectionPath: string[]
  targets: SettleTarget[]
  getTargets?: () => SettleTarget[]
  createRemoveOperation?: (nodeId: string) => RemoveFromConnectionOperation
  /** Keep the optimistic operation pending while the host completes post-delete reconciliation. */
  settleAfterOnCompleted?: boolean
}

export type AccountIssueOption = {
  isAccountIssue: boolean
  permalink: string
}

export type OptionConfig = {
  /** function to (soft) navigate to a given url, optionally with router state */
  navigate: (url: string, options?: {state?: {viewNav: ViewNavState}}) => void
  /** preloaded queries to use for the issue viewer - coming from the relay entrypoint */
  preloadedQueries?: IssueViewerPreloadedQueries
  /** function to be called when a link is clicked */
  onLinkClick?: (event: MouseEvent) => void
  /** function to be called when the close button in the actions toolbar is clicked */
  onClose?: () => void
  /** function to navigate back to the previous page */
  navigateBack?: () => void
  /** the graphQL id of the project to open in the sidebar */
  selectedProjectId?: string
  /** the owner of the projects to include in the sidebar */
  allowedProjectOwner?: string
  onRemovedFromProject: () => void
  showRepositoryPill?: boolean
  /** Overrides the create sub-issue dialog title. */
  subIssueCreateDialogTitle?: string
  showIssueCreateButton?: boolean
  customEditMenuEntries?: React.ReactElement[]
  shouldSkipSetDocumentTitle?: boolean
  /** Fetching policies for the queries related to the issue number */
  issueQueriesFetchingPolicy?: UseQueryLoaderLoadQueryOptions
  /** callback when there was an update to the issue (except adding a comment) */
  onIssueUpdate: () => void
  /** callback when a new comment was added to the issue */
  onNewIssueComment?: () => void
  timelineEventBaseUrl?: string
  /** callback when the issue was deleted */
  onIssueDelete?: (issueId: string, options?: {alreadySettled?: boolean}) => void | Promise<void>
  /** The embedding collection that owns optimistic deletion and confirmed settlement. */
  issueDeletionConnection?: IssueDeletionConnection
  /** additional actions to render in the header menu */
  additionalHeaderActions?: React.ReactNode
  /**
   * Called when an edit for a comment is canceled (e.g., user cleared the comment textarea)
   * @param commentId A unique ID for the given comment edit - either a new comment or existing comment
   */
  onCommentEditCancel?: (commentId: string) => void
  /**
   * Called when an edit for a comment is started (e.g., user started typing in the comment box)
   * @param commentId A unique ID for the given comment edit - either a new comment or existing comment
   */
  onCommentEditStart?: (commentId: string) => void
  /**
   * Called whenever the issue edit state changes (e.g., start changing body, canceled editing body)
   * @param isEditing Whether or not the issue body is currently being edited
   */
  onIssueEditStateChange?: (isEditing: boolean) => void
  /** Whether clicking the issue title in the sticky header should scroll to the top of the page or navigate away */
  scrollToTopOnClick?: boolean
  /** Canonical permalink and account issue classification used when building issue URLs. */
  accountIssueOption?: AccountIssueOption
  /** Used to control the behavior of clicking on a sub-issue in the sub-issue list view i.e. open in side-panel */
  onSubIssueClick?: (
    subIssueItem: SubIssueSidePanelItem,
    options?: {
      fromSuccessfulOptimisticUpdate?: boolean
      issueDeletionConnection?: IssueDeletionConnection
    },
  ) => void
  /** Used to control the behavior of clicking on the parent issue in the issue header i.e. close the side-panel */
  onParentIssueActivate?: (e: React.MouseEvent | React.KeyboardEvent, parentItemIdentifier: ItemIdentifier) => boolean
  /**
   * If true, use CSS Viewport queries for responsive layout. If false, use JS width listener.
   * @note This assumes that the IssueViewer is mounted to the root of the app and not inside a container.
   */
  useViewportQueries?: boolean
  /**
   * If true, indicates that the issue-viewer is within the currently opened side-panel. This is done
   * so that we have a quick way to determine whether the component accessing this is inside
   * of the side-panel or not without the need to search the dom with something like `.contains()`.
   * If false, the issue-viewer is not inside the open side-panel.
   * If undefined, the side panel is not open.
   */
  insideSidePanel?: boolean
  // Used for different error handling
  subIssueFromOptimisticUpdate?: boolean
  /**
   * If true, the entire issue viewer is readonly:
   * - user cannot edit anything via the "..." menu
   * - the sidebar is readonly
   * - the comment box at the end of the timeline is not shown
   * - buttons to edit title, add reactions, etc. are not shown
   */
  isReadonly?: boolean
  /**
   * If true, the issue timeline and comment thread is not shown.
   */
  hideTimeline?: boolean
  /**
   * Called when the issue viewer has loaded and rendered the issue data.
   * Useful for performance tracking.
   */
  onLoad?: () => void
  /**
   * Called when the user presses j to navigate to the next item in the list.
   * Used for keyboard navigation in the side panel.
   */
  onNavigateToNextItem?: () => void
  /**
   * Called when the user presses k to navigate to the previous item in the list.
   * Used for keyboard navigation in the side panel.
   */
  onNavigateToPreviousItem?: () => void
  viewNavState?: ViewNavState | null
  onLoadMoreNavItems?: () => void
  onLoadBackNavItems?: () => void
  /**
   * Optional self-serve recruitment/feedback survey banner rendered at the very top of the
   * issue viewer. Sourced from an SSR/embedded payload populated by the server (github/github).
   * When absent, no banner is rendered, keeping this fully backwards-compatible.
   */
  copilotSurveyBanner?: CopilotSurveyProps
}

export const ISSUE_VIEWER_DEFAULT_CONFIG: OptionConfig = {
  navigate: noop,
  navigateBack: noop,
  onIssueUpdate: noop,
  onNewIssueComment: noop,
  onRemovedFromProject: noop,
  preloadedQueries: undefined,
  onLinkClick: noop,
  onClose: noop,
  showRepositoryPill: undefined,
  selectedProjectId: undefined,
  allowedProjectOwner: undefined,
  customEditMenuEntries: undefined,
  shouldSkipSetDocumentTitle: false,
  issueQueriesFetchingPolicy: undefined,
  timelineEventBaseUrl: undefined,
  accountIssueOption: undefined,
  useViewportQueries: false,
  isReadonly: false,
  hideTimeline: false,
}
